import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { ArrowLeft, Download, Loader2, Sparkles, Send, FileImage, Edit3, Paintbrush, Save, Share2, FileDown, Play, Undo2, Crosshair, X, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { withBackend } from '../utils/api';
import { supabaseHelpers } from '../lib/supabase';
import ShareContentModal from './ShareContentModal';
import { getDefaultModel } from '../utils/models';

const InfographicWorkspace = forwardRef(function InfographicWorkspace({ preloadedData, onBack, onFullscreen, userId, onComplete, onUpdate, existingProjectId, externalChatMode, onChatMessage, chatMessages, onUsageRefresh }, ref) {
  const hasSavedContent = !!(preloadedData?.savedHtml || preloadedData?.savedImage);
  const [phase, setPhase] = useState(hasSavedContent ? 'preview' : 'generating'); // generating | streaming | preview | error
  const [htmlContent, setHtmlContent] = useState(preloadedData?.savedHtml || '');
  const [infographicImage, setInfographicImage] = useState(preloadedData?.savedImage || ''); // For Imaginative mode — single full image
  const [title, setTitle] = useState(preloadedData?.savedTitle || '');
  const [editPrompt, setEditPrompt] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [editMode, setEditMode] = useState('view'); // view | text | style
  const [isSaving, setIsSaving] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPresentMode, setShowPresentMode] = useState(false);
  const [projectId, setProjectId] = useState(existingProjectId || null);
  const [chatHistory, setChatHistory] = useState([]);
  const [generationStatus, setGenerationStatus] = useState('');
  // Undo history
  const [htmlHistory, setHtmlHistory] = useState([]);
  // Region selection
  const [regionMode, setRegionMode] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [regionStart, setRegionStart] = useState(null);
  const [regionCurrent, setRegionCurrent] = useState(null);

  const iframeRef = useRef(null);
  const editTextareaRef = useRef(null);
  const chatEndRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const hasAutoSavedRef = useRef(!!existingProjectId);
  const isSaveUpdateRef = useRef(!!hasSavedContent);
  const thumbnailRef = useRef(null);
  const infographicContainerRef = useRef(null);

  // Extract data from preloaded
  const outline = preloadedData?.outline;
  const questionnaireData = preloadedData?.questionnaireData;
  const flowConfig = preloadedData?.flowConfig;

  const isImageMode = flowConfig?.generationMode === 'infographic-ai';

  // Canvas geometry follows the clarifier's size answer: web (1200 wide,
  // auto height), story (1080x1920 fixed), a4 (794x1123 fixed print frame).
  const canvasSize = questionnaireData?.infographicSize || flowConfig?.infographicSize || 'web';
  const canvasWidth = canvasSize === 'story' ? 1080 : canvasSize === 'a4' ? 794 : 1200;
  const canvasFixedHeight = canvasSize === 'story' ? 1920 : canvasSize === 'a4' ? 1123 : null;

  // Auto-generate on mount (skip if loading saved project)
  useEffect(() => {
    if (hasSavedContent) return; // Already loaded from save
    if (outline?.slides?.length > 0) {
      setTitle(outline.title || 'Infographic');
      generateInfographic(outline.slides);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hide nav in fullscreen
  useEffect(() => {
    onFullscreen?.(true);
    return () => onFullscreen?.(false);
  }, [onFullscreen]);

  // Ctrl+Z undo + Delete key support
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && editMode === 'edit') {
        const activeEl = document.activeElement;
        if (activeEl?.tagName === 'INPUT' || activeEl?.tagName === 'TEXTAREA') return;
        // Check if an element is selected inside the iframe
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return;
        const selected = doc.querySelector('[style*="outline: 2px solid"]');
        if (selected && selected !== doc.body && selected !== doc.documentElement) {
          e.preventDefault();
          pushToHistory(htmlContent);
          selected.remove();
          serializeAndUpdateHtml();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htmlHistory, editMode, htmlContent]);

  const pushToHistory = useCallback((html) => {
    if (html) setHtmlHistory(prev => [...prev.slice(-20), html]); // Keep max 20 states
  }, []);

  const handleUndo = useCallback(() => {
    if (htmlHistory.length === 0) { toast.info('Nothing to undo'); return; }
    const previous = htmlHistory[htmlHistory.length - 1];
    setHtmlHistory(prev => prev.slice(0, -1));
    setHtmlContent(previous);
    toast.success('Undone');
  }, [htmlHistory]);

  const generateInfographic = async (sections) => {
    setPhase('generating');
    setGenerationStatus('Preparing...');
    thumbnailRef.current = null;

    try {
      // AI image mode: use non-streaming endpoint
      if (isImageMode) {
        const response = await fetch(withBackend('/api/generate-infographic-html'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sections,
            title: outline?.title || 'Infographic',
            visual_style: questionnaireData?.visualStyle || 'modern-colorful',
            html_model: flowConfig?.htmlModel || getDefaultModel(),
            logo_url: flowConfig?.logoUrl || '',
            language: flowConfig?.language || 'en',
            generation_mode: 'ai',
            user_prompt: flowConfig?.prompt || '',
            web_research: flowConfig?.webResearch || '',
            special_instructions: flowConfig?.specialInstructions || questionnaireData?.specialInstructions || '',
            brand_identity_id: flowConfig?.brandIdentityId || '',
            user_id: userId || '',
            design_reference_images: flowConfig?.designReferenceImages || [],
            canvas_size: canvasSize,
          }),
        });
        if (response.status === 403) {
          const err = await response.json().catch(() => ({}));
          if (err.error === 'usage_limit_exceeded') {
            toast.error(err.details?.reason === 'trial_expired'
              ? 'Your 7-day trial has ended. Upgrade to keep creating.'
              : 'You\'ve reached your monthly limit for infographic generation. Upgrade for more.');
            setPhase('preview');
            return;
          }
        }
        if (!response.ok) throw new Error((await response.json()).error || 'Failed to generate');
        const data = await response.json();
        setInfographicImage(data.image);
        setHtmlContent('');
        thumbnailRef.current = data.image;
        setPhase('preview');
        onUsageRefresh?.();
        return;
      }

      // HTML mode: use SSE streaming endpoint
      const response = await fetch(withBackend('/api/generate-infographic-html-stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sections,
          title: outline?.title || 'Infographic',
          visual_style: questionnaireData?.visualStyle || 'modern-colorful',
          chart_types: questionnaireData?.chartTypes || 'mixed',
          audience: questionnaireData?.audience || '',
          infographic_type: questionnaireData?.infographicType || '',
          html_model: flowConfig?.htmlModel || getDefaultModel(),
          logo_url: flowConfig?.logoUrl || '',
          language: flowConfig?.language || 'en',
          generation_mode: 'html',
          user_prompt: flowConfig?.prompt || '',
          web_research: flowConfig?.webResearch || '',
          excel_data: flowConfig?.excelData || null,
          special_instructions: flowConfig?.specialInstructions || questionnaireData?.specialInstructions || '',
          brand_identity_id: flowConfig?.brandIdentityId || '',
          user_id: userId || '',
          design_reference_images: flowConfig?.designReferenceImages || [],
          // The QA answer was previously collected and never sent; the
          // metaphor comes from the strategic plan when one exists.
          run_qa: flowConfig?.runQa !== 'no',
          visual_metaphor: flowConfig?.plan?.visual_metaphor || questionnaireData?.visualMetaphor || '',
          art_direction: flowConfig?.artDirection || '',
          canvas_size: canvasSize,
        }),
      });

      if (response.status === 403) {
        const err = await response.json().catch(() => ({}));
        if (err.error === 'usage_limit_exceeded') {
          toast.error('You\'ve reached your monthly limit for infographic generation. Upgrade for more.');
          setPhase('preview');
          return;
        }
      }
      if (!response.ok) throw new Error('Failed to start generation');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            switch (event.type) {
              case 'chunk':
                setPhase('streaming');
                setHtmlContent(event.text);
                setInfographicImage('');
                break;
              case 'complete':
                setHtmlContent(event.html);
                setInfographicImage('');
                setPhase('preview');
                onUsageRefresh?.();
                onChatMessage?.('Infographic generated! You can now edit it or ask questions about it.');
                break;
              case 'progress':
                // Post-processing status (illustrations / quality check).
                if (event.message) onChatMessage?.(event.message);
                break;
              case 'qa_update':
                // Illustrations resolved and/or QA fixes applied after the
                // first complete — swap in the final HTML.
                if (event.html) setHtmlContent(event.html);
                break;
              case 'thumbnail':
                thumbnailRef.current = event.preview_url;
                break;
              case 'error':
                throw new Error(event.error);
              default:
                break;
            }
          } catch (parseErr) {
            if (parseErr.message && !parseErr.message.includes('Unexpected token'))
              throw parseErr;
          }
        }
      }
    } catch (error) {
      console.error('Infographic generation error:', error);
      toast.error(error.message || 'Failed to generate infographic');
      setPhase('error');
    }
  };

  // Write HTML to iframe when content changes (HTML mode only)
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !htmlContent) return;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    doc.open();
    doc.write(htmlContent);
    doc.close();

    // Auto-resize iframe to content height
    const resizeIframe = () => {
      try {
        const body = doc.body;
        const html = doc.documentElement;
        if (body && html) {
          const height = Math.max(
            body.scrollHeight, body.offsetHeight,
            html.clientHeight, html.scrollHeight, html.offsetHeight
          );
          iframe.style.height = height + 'px';
        }
      } catch {}
    };

    setTimeout(resizeIframe, 500);
    setTimeout(resizeIframe, 1500);
    setTimeout(resizeIframe, 3000);

    // Re-apply edit mode after rewrite
    if (editMode === 'edit') {
      setTimeout(() => { enableTextEditing(); enableStylePicker(); }, 600);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htmlContent]);

  // ─── HTML mode editing functions ───

  // Auto-save debounce after inline edits
  const editSaveTimerRef = useRef(null);
  const scheduleEditAutoSave = useCallback(() => {
    if (editSaveTimerRef.current) clearTimeout(editSaveTimerRef.current);
    editSaveTimerRef.current = setTimeout(() => {
      const iframe = iframeRef.current;
      const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
      if (!doc) return;
      const clonedDoc = doc.cloneNode(true);
      clonedDoc.querySelectorAll('[contenteditable]').forEach(el => el.removeAttribute('contenteditable'));
      const pickerStyle = clonedDoc.getElementById('style-picker-css');
      if (pickerStyle) pickerStyle.remove();
      clonedDoc.querySelectorAll('*').forEach(el => {
        if (el.style.outline) el.style.removeProperty('outline');
        if (el.style.outlineOffset) el.style.removeProperty('outline-offset');
        if (el.style.cursor === 'text') el.style.removeProperty('cursor');
        if (el.getAttribute('style') === '') el.removeAttribute('style');
      });
      const serialized = '<!DOCTYPE html>' + clonedDoc.documentElement.outerHTML;
      isSaveUpdateRef.current = true;
      setHtmlContent(serialized);
    }, 2000);
  }, []);

  const enableTextEditing = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;

    const textElements = doc.querySelectorAll('h1, h2, h3, h4, h5, h6, p, span, li, td, th, a, label, figcaption');
    textElements.forEach(el => {
      el.contentEditable = 'true';
      el.style.outline = 'none';
      el.style.cursor = 'text';
      el.addEventListener('focus', () => {
        el.style.outline = '2px solid #3b82f6';
        el.style.outlineOffset = '2px';
        el.style.borderRadius = '2px';
      });
      el.addEventListener('blur', () => {
        el.style.outline = 'none';
        scheduleEditAutoSave();
      });
      el.addEventListener('input', () => {
        scheduleEditAutoSave();
      });
    });
  }, [scheduleEditAutoSave]);

  const disableTextEditing = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;

    const textElements = doc.querySelectorAll('[contenteditable="true"]');
    textElements.forEach(el => {
      el.contentEditable = 'false';
      el.style.outline = 'none';
      el.style.cursor = '';
    });
  }, []);

  const enableStylePicker = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;

    const script = doc.createElement('script');
    script.textContent = `
      (function() {
        if (window.__stylePickerActive) return;
        window.__stylePickerActive = true;
        var selected = null;
        document.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          if (selected) selected.style.outline = '';
          selected = e.target;
          selected.style.outline = '2px solid #8b5cf6';
          selected.style.outlineOffset = '2px';
        }, true);
        document.addEventListener('mouseover', function(e) {
          if (e.target !== selected) {
            e.target.style.outline = '1px dashed #8b5cf680';
          }
        }, true);
        document.addEventListener('mouseout', function(e) {
          if (e.target !== selected) {
            e.target.style.outline = '';
          }
        }, true);
      })();
    `;
    doc.body.appendChild(script);
  }, []);

  const disableStylePicker = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;
    doc.querySelectorAll('*').forEach(el => { el.style.outline = ''; });
    if (doc.defaultView) doc.defaultView.__stylePickerActive = false;
  }, []);

  const handleEditToggle = () => {
    if (editMode === 'edit') {
      // Exiting edit mode — disable both text editing and style picker, serialize
      disableTextEditing();
      disableStylePicker();
      serializeAndUpdateHtml();
      setEditMode('view');
    } else {
      // Entering edit mode — enable both text editing and style picker
      pushToHistory(htmlContent); // Save state before editing
      setEditMode('edit');
      setTimeout(() => {
        enableTextEditing();
        enableStylePicker();
      }, 100);
    }
  };

  const serializeAndUpdateHtml = () => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;

    doc.querySelectorAll('[contenteditable]').forEach(el => {
      el.removeAttribute('contenteditable');
      el.style.outline = '';
      el.style.cursor = '';
    });
    doc.querySelectorAll('*').forEach(el => { el.style.outline = ''; });

    const serialized = '<!DOCTYPE html>' + doc.documentElement.outerHTML;
    setHtmlContent(serialized);
  };

  // ─── Region Selection ───

  const handleRegionMouseDown = (e) => {
    if (!regionMode) return;
    const rect = infographicContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setRegionStart({ x, y });
    setRegionCurrent({ x, y });
    setSelectedRegion(null);
  };

  const handleRegionMouseMove = (e) => {
    if (!regionMode || !regionStart) return;
    const rect = infographicContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100));
    setRegionCurrent({ x, y });
  };

  const handleRegionMouseUp = () => {
    if (!regionMode || !regionStart || !regionCurrent) return;
    const x = Math.min(regionStart.x, regionCurrent.x);
    const y = Math.min(regionStart.y, regionCurrent.y);
    const width = Math.abs(regionCurrent.x - regionStart.x);
    const height = Math.abs(regionCurrent.y - regionStart.y);
    if (width > 2 && height > 2) {
      setSelectedRegion({ x, y, width, height });
      setRegionMode(false);
      toast.success('Region selected — describe your edit in the chat');
    }
    setRegionStart(null);
    setRegionCurrent(null);
  };

  const getRegionStyle = () => {
    if (!regionStart || !regionCurrent) return null;
    const x = Math.min(regionStart.x, regionCurrent.x);
    const y = Math.min(regionStart.y, regionCurrent.y);
    const width = Math.abs(regionCurrent.x - regionStart.x);
    const height = Math.abs(regionCurrent.y - regionStart.y);
    return { left: `${x}%`, top: `${y}%`, width: `${width}%`, height: `${height}%` };
  };

  // ─── Expose methods for UnifiedWorkspace toolbar ───

  useImperativeHandle(ref, () => ({
    sendEdit: (message, options) => handleEditRequest(message, options),
    getChatHistory: () => chatHistory,
    getData: () => ({ html: htmlContent, title }),
    save: () => handleSave(),
    share: () => setShowShareModal(true),
    present: () => setShowPresentMode(true),
    exportPng: () => handleDownloadPNG(),
    exportPdf: () => handleDownloadPDF(),
    exportHtml: () => handleDownloadHTML(),
    getExportState: () => ({ exporting: false, saving: isSaving }),
    edit: () => handleEditToggle(),
    selectRegion: () => { setRegionMode(true); setSelectedRegion(null); toast.info('Draw a region on the infographic'); },
    undo: () => handleUndo(),
    getEditMode: () => editMode,
    getRegionMode: () => regionMode,
    getSelectedRegion: () => selectedRegion,
    clearRegion: () => { setSelectedRegion(null); setRegionMode(false); },
  }));

  // ─── Shared functions ───

  const handleEditRequest = async (messageOverride, options = {}) => {
    const request = (messageOverride || editPrompt).trim();
    if (!request) return;
    setEditPrompt('');
    setIsRegenerating(true);

    // Save current state for undo
    pushToHistory(htmlContent);

    setChatHistory(prev => [...prev, { role: 'user', content: request, timestamp: Date.now() }]);

    try {
      if (isImageMode) {
        // Image mode: re-generate from modified outline
        const modifiedSections = outline?.slides?.map(s => ({
          ...s,
          instructions: `${s.instructions || ''} EDIT REQUEST: ${request}`,
        }));
        await generateInfographic(modifiedSections || []);
        setChatHistory(prev => [...prev, { role: 'bot', content: 'Infographic regenerated with your changes.', timestamp: Date.now() }]);
        onChatMessage?.('Infographic regenerated with your changes.');
      } else {
        // HTML mode: in-place edit via /edit-html-slide
        const currentHtml = serializeHtml();
        const response = await fetch(withBackend('/edit-html-slide'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            request_id: crypto.randomUUID(),
            html_source: currentHtml,
            edit_prompt: request,
            html_model: flowConfig?.htmlModel || getDefaultModel(),
            highlight_region: selectedRegion || undefined,
            reference_images: options.referenceImages || undefined,
            content_type: 'infographic',
            brand_identity_id: flowConfig?.brandIdentityId || '',
            user_id: userId || '',
            run_qa: flowConfig?.runQa !== 'no',
          }),
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.message || errData.error || 'Failed to update infographic');
        }

        const data = await response.json();
        if (data.html_source) {
          setHtmlContent(data.html_source);
          setChatHistory(prev => [...prev, { role: 'bot', content: 'Infographic updated.', timestamp: Date.now() }]);
          onChatMessage?.('Infographic updated.');
        }
      }
      // Clear region after edit
      setSelectedRegion(null);
      setRegionMode(false);
      toast.success('Infographic updated!');
    } catch {
      toast.error('Failed to update infographic');
      setChatHistory(prev => [...prev, { role: 'bot', content: 'Failed to update. Please try again.', timestamp: Date.now(), error: true }]);
      onChatMessage?.('Failed to update. Please try again.');
    } finally {
      setIsRegenerating(false);
    }
  };

  // Serialize current iframe HTML (clean up edit artifacts)
  const serializeHtml = () => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return htmlContent;
    doc.querySelectorAll('[contenteditable]').forEach(el => {
      el.removeAttribute('contenteditable');
      el.style.outline = '';
      el.style.cursor = '';
    });
    doc.querySelectorAll('*').forEach(el => { el.style.outline = ''; });
    return '<!DOCTYPE html>' + doc.documentElement.outerHTML;
  };

  const handleSave = () => {
    if (!htmlContent && !infographicImage) {
      toast.error('Nothing to save');
      return;
    }
    setIsSaving(true);
    try {
      if (!isImageMode) {
        const serialized = serializeHtml();
        isSaveUpdateRef.current = true;
        setHtmlContent(serialized);
      }
      if (editMode === 'edit') {
        disableTextEditing();
        disableStylePicker();
        setEditMode('view');
      }
      handleSaveProject();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  // Save project to Supabase
  const handleSaveProject = async () => {
    if (!userId) return;
    const currentHtml = serializeHtml();
    if (!currentHtml && !infographicImage) return;

    try {
      const infographicData = {
        type: 'infographic',
        html_content: currentHtml,
        image_content: infographicImage || null,
        title: title,
        is_image_mode: isImageMode,
        section_count: outline?.slides?.length || 0,
        metadata: {
          generationMode: flowConfig?.generationMode,
          questionnaireData: questionnaireData,
          updated_at: new Date().toISOString(),
          chatHistory: chatMessages || [],
        }
      };

      let thumb = isImageMode ? infographicImage : thumbnailRef.current;
      if (!thumb && htmlContent) {
        try {
          const resp = await fetch(withBackend('/screenshot-html'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ html_source: htmlContent, width: canvasWidth, full_page: true }),
          });
          if (resp.ok) {
            const result = await resp.json();
            thumb = result.preview_url;
            thumbnailRef.current = thumb;
          }
        } catch (e) { console.warn('Thumbnail generation failed:', e); }
      }

      if (projectId) {
        const { error } = await supabaseHelpers.updateProject(projectId, {
          name: title,
          analysis_data: infographicData,
          status: 'completed',
          ...(thumb ? { thumbnail_url: thumb } : {}),
        });
        if (error) throw error;
        if (onUpdate) onUpdate(projectId, infographicData);
        toast.success('Project updated!');
      } else {
        const { data, error } = await supabaseHelpers.createProject({
          user_id: userId,
          name: title,
          client_name: 'Infographic',
          status: 'completed',
          slide_count: outline?.slides?.length || 1,
          analysis_data: infographicData,
          ...(thumb ? { thumbnail_url: thumb } : {}),
        });
        if (error) throw error;
        if (data) {
          setProjectId(data.id);
          hasAutoSavedRef.current = true;
          if (onComplete) onComplete({
            projectId: data.id,
            infographicData,
            preview_url: thumbnailRef.current || infographicImage,
          });
          toast.success('Infographic saved!');
        }
      }
    } catch (error) {
      console.error('Save project error:', error);
      toast.error('Failed to save project');
    }
  };

  // Autosave after generation completes or content changes
  useEffect(() => {
    if (phase !== 'preview' || !userId) return;
    if (isSaveUpdateRef.current) { isSaveUpdateRef.current = false; return; }
    if (!htmlContent && !infographicImage) return;

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);

    autosaveTimerRef.current = setTimeout(() => {
      handleSaveProject();
    }, 5000);

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [htmlContent, infographicImage, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Download as PNG
  const handleDownloadPNG = async () => {
    if (isImageMode && infographicImage) {
      const link = document.createElement('a');
      link.download = `${title || 'infographic'}.png`;
      link.href = infographicImage;
      link.click();
      toast.success('PNG downloaded!');
      return;
    }

    if (!htmlContent) return;
    toast.info('Generating PNG...');

    try {
      const response = await fetch(withBackend('/screenshot-html'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          html_source: htmlContent,
          width: canvasWidth,
          full_page: true,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.screenshot) {
          const link = document.createElement('a');
          link.download = `${title || 'infographic'}.png`;
          link.href = `data:image/png;base64,${data.screenshot}`;
          link.click();
          toast.success('PNG downloaded!');
        }
      } else {
        handleDownloadHTML();
      }
    } catch {
      handleDownloadHTML();
    }
  };

  // Download HTML (HTML mode only)
  const handleDownloadHTML = () => {
    if (!htmlContent) return;
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `${title || 'infographic'}.html`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('HTML downloaded!');
  };

  // Download as PDF
  const handleDownloadPDF = async () => {
    if (!htmlContent && !infographicImage) return;
    toast.loading('Generating PDF...', { id: 'pdf-export' });

    try {
      if (isImageMode && infographicImage) {
        const { jsPDF } = await import('jspdf');
        const img = new Image();
        img.src = infographicImage;
        await new Promise(r => { img.onload = r; });
        const isPortrait = img.height > img.width;
        const pdf = new jsPDF({ orientation: isPortrait ? 'portrait' : 'landscape', unit: 'px', format: [img.width, img.height] });
        pdf.addImage(infographicImage, 'PNG', 0, 0, img.width, img.height);
        pdf.save(`${title || 'infographic'}.pdf`);
      } else {
        // Export as ONE PDF page at the canvas's true geometry. Fixed
        // frames (story/a4) use their exact dimensions; the web canvas is
        // a tall continuous page, so measure the rendered height (the old
        // fixed 1200x1600 request paginated it into arbitrary A4-ish
        // chunks — hero cut across pages, white bands between sections).
        let exportHeight = canvasFixedHeight;
        if (!exportHeight) {
          const doc = iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document;
          exportHeight = Math.max(
            doc?.body?.scrollHeight || 0,
            doc?.documentElement?.scrollHeight || 0,
            1600
          );
        }
        const response = await fetch(withBackend('/export-slides-as-pdf'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            slides: [htmlContent],
            presentation_title: title || 'infographic',
            width: canvasWidth,
            height: exportHeight,
            single_page: true,
            orientation: 'portrait',
          }),
        });
        if (!response.ok) throw new Error('PDF generation failed');
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${title || 'infographic'}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
      toast.success('PDF downloaded!', { id: 'pdf-export' });
    } catch (err) {
      console.error('PDF export error:', err);
      handleDownloadHTML();
      toast.info('PDF export unavailable — HTML downloaded instead', { id: 'pdf-export' });
    }
  };

  const hasContent = isImageMode ? !!infographicImage : !!htmlContent;

  return (
    <div className={`${externalChatMode ? 'h-full' : 'h-screen'} flex flex-col ${externalChatMode ? 'bg-muted/30' : 'bg-background'}`}>
      {/* Header — only shown when NOT in external chat mode (UnifiedWorkspace provides its own toolbar) */}
      {!externalChatMode && (
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border/50 bg-card/50 backdrop-blur-sm">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-foreground truncate">{title}</h2>
            <p className="text-xs text-muted-foreground">
              {phase === 'generating' || phase === 'streaming'
                ? (isImageMode ? 'Generating AI images...' : 'Generating infographic...')
                : `${outline?.slides?.length || 0} sections${isImageMode ? ' (Imaginative)' : ''}`}
            </p>
          </div>
          {phase === 'preview' && (
            <div className="flex items-center gap-2">
              {/* Edit button — HTML mode only, enables both text + style editing */}
              {!isImageMode && (
                <>
                  <Button
                    size="sm"
                    variant={editMode === 'edit' ? 'default' : 'outline'}
                    className={editMode === 'edit' ? 'bg-blue-500 hover:bg-blue-600 text-white' : ''}
                    onClick={handleEditToggle}
                  >
                    <Edit3 className="w-3.5 h-3.5 mr-1.5" /> {editMode === 'edit' ? 'Done' : 'Edit'}
                  </Button>
                  <Button
                    size="sm"
                    variant={regionMode ? 'default' : 'outline'}
                    className={regionMode ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}
                    onClick={() => { setRegionMode(!regionMode); setSelectedRegion(null); }}
                  >
                    <Crosshair className="w-3.5 h-3.5 mr-1.5" /> Select Region
                  </Button>
                  {editMode === 'edit' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => {
                        const doc = iframeRef.current?.contentDocument;
                        if (!doc) return;
                        const selected = doc.querySelector('[style*="outline: 2px solid"]');
                        if (selected && selected !== doc.body && selected !== doc.documentElement) {
                          pushToHistory(htmlContent);
                          selected.remove();
                          serializeAndUpdateHtml();
                        }
                      }}
                      title="Delete selected element (Del)"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {htmlHistory.length > 0 && (
                    <Button size="sm" variant="outline" onClick={handleUndo}>
                      <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Undo
                    </Button>
                  )}
                  <div className="w-px h-6 bg-border/50 mx-1" />
                </>
              )}
              <Button variant="outline" size="sm" onClick={handleSave} disabled={isSaving}>
                <Save className={`w-3.5 h-3.5 mr-1.5 ${isSaving ? 'animate-spin' : ''}`} /> Save
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowShareModal(true)}>
                <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share
              </Button>
              <Button size="sm" onClick={handleDownloadPNG} className="bg-gradient-to-r from-purple-500 to-violet-500 text-white hover:from-purple-600 hover:to-violet-600">
                <FileImage className="w-3.5 h-3.5 mr-1.5" /> PNG
              </Button>
              <Button size="sm" onClick={handleDownloadPDF} className="bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600">
                <FileDown className="w-3.5 h-3.5 mr-1.5" /> PDF
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Edit mode indicator — HTML mode only */}
      {!isImageMode && editMode === 'edit' && (
        <div className="px-4 py-1.5 text-xs text-white text-center bg-blue-500">
          Click text to edit · Click elements to select · Del to delete · Ctrl+Z to undo
        </div>
      )}

      {/* Region selection indicator */}
      {regionMode && (
        <div className="px-4 py-1.5 text-xs text-white text-center bg-orange-500 flex items-center justify-center gap-2">
          Draw a rectangle on the area you want to edit
          <button onClick={() => { setRegionMode(false); setSelectedRegion(null); }} className="underline hover:no-underline">Cancel</button>
        </div>
      )}
      {selectedRegion && !regionMode && (
        <div className="px-4 py-1.5 text-xs text-white text-center bg-orange-500/80 flex items-center justify-center gap-2">
          Region selected — describe your edit in the chat
          <button onClick={() => setSelectedRegion(null)} className="underline hover:no-underline">Clear</button>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Infographic Preview */}
        <div className="flex-1 overflow-y-auto bg-slate-100 dark:bg-slate-900">
          {phase === 'generating' && (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="relative">
                <Loader2 className="w-12 h-12 text-purple-500 animate-spin" />
                <Sparkles className="w-5 h-5 text-purple-400 absolute -top-1 -right-1 animate-pulse" />
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                {generationStatus || (isImageMode ? 'AI is designing your infographic...' : 'Generating infographic sections...')}
              </p>
            </div>
          )}

          {/* Streaming preview — shows partial HTML as it's being generated */}
          {phase === 'streaming' && !isImageMode && htmlContent && (
            <div className="flex justify-center py-6 relative">
              <div className="absolute top-8 left-1/2 -translate-x-1/2 z-10 bg-purple-500/90 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-2 shadow-lg backdrop-blur-sm">
                <Loader2 className="w-3 h-3 animate-spin" /> Generating sections...
              </div>
              <div className="bg-white rounded-lg shadow-xl overflow-hidden" style={{ width: canvasWidth }}>
                <iframe
                  ref={iframeRef}
                  title="Infographic Preview"
                  style={{ width: canvasWidth, minHeight: canvasFixedHeight || 800, border: 'none', display: 'block' }}
                  sandbox="allow-scripts allow-same-origin"
                />
              </div>
            </div>
          )}

          {phase === 'error' && (
            <div className="flex flex-col items-center justify-center h-full">
              <p className="text-sm text-red-500 mb-3">Failed to generate infographic</p>
              <Button variant="outline" onClick={() => generateInfographic(outline?.slides || [])}>
                Try Again
              </Button>
            </div>
          )}

          {/* HTML mode preview */}
          {phase === 'preview' && !isImageMode && htmlContent && (
            <div className="flex justify-center py-6">
              <div
                ref={infographicContainerRef}
                className="bg-white rounded-lg shadow-xl overflow-hidden relative"
                style={{ width: canvasWidth }}
                onMouseDown={handleRegionMouseDown}
                onMouseMove={handleRegionMouseMove}
                onMouseUp={handleRegionMouseUp}
              >
                <iframe
                  ref={iframeRef}
                  title="Infographic Preview"
                  style={{ width: canvasWidth, minHeight: canvasFixedHeight || 800, border: 'none', display: 'block', pointerEvents: regionMode ? 'none' : 'auto' }}
                  sandbox="allow-scripts allow-same-origin"
                />
                {/* Region selection overlay */}
                {regionMode && regionStart && regionCurrent && (
                  <div className="absolute border-2 border-orange-500 bg-orange-500/10 pointer-events-none" style={getRegionStyle()} />
                )}
                {selectedRegion && !regionMode && (
                  <div
                    className="absolute border-2 border-orange-500 bg-orange-500/10 pointer-events-none"
                    style={{ left: `${selectedRegion.x}%`, top: `${selectedRegion.y}%`, width: `${selectedRegion.width}%`, height: `${selectedRegion.height}%` }}
                  />
                )}
              </div>
            </div>
          )}

          {/* Image mode preview — single full infographic image */}
          {phase === 'preview' && isImageMode && infographicImage && (
            <div className="flex justify-center py-6">
              <div className="bg-white rounded-lg shadow-xl overflow-hidden" style={{ maxWidth: 1200 }}>
                <img
                  src={infographicImage}
                  alt={title || 'Infographic'}
                  style={{ width: '100%', display: 'block' }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Right Panel — Edit with AI (hidden in external chat mode) */}
        {phase === 'preview' && !externalChatMode && (
          <div className="w-[320px] border-l border-border/50 flex flex-col bg-card/50">
            <div className="px-4 py-3 border-b border-border/50">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-500" />
                Edit with AI
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3">
              {chatHistory.length === 0 ? (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground mb-3">Quick suggestions:</p>
                  {(isImageMode ? [
                    'Make it more vibrant and colorful',
                    'Use a darker color scheme',
                    'Add more detail to the data sections',
                    'Make the typography bolder',
                  ] : [
                    'Make the header section more colorful',
                    'Add more data visualizations',
                    'Make the text more concise',
                    'Change to dark theme',
                  ]).map((suggestion, i) => (
                    <button
                      key={i}
                      onClick={() => setEditPrompt(suggestion)}
                      className="block w-full text-left text-xs px-3 py-2 rounded-lg border border-border/50 hover:border-purple-400/50 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors text-muted-foreground"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {chatHistory.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] text-xs px-3 py-2 rounded-lg ${
                        msg.role === 'user'
                          ? 'bg-purple-500 text-white'
                          : msg.error
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                            : 'bg-muted text-foreground'
                      }`}>
                        {msg.role === 'bot' && <Sparkles className="w-3 h-3 inline mr-1 opacity-60" />}
                        {msg.content}
                      </div>
                    </div>
                  ))}
                  {isRegenerating && (
                    <div className="flex justify-start">
                      <div className="bg-muted text-foreground text-xs px-3 py-2 rounded-lg flex items-center gap-2">
                        <Loader2 className="w-3 h-3 animate-spin" /> Updating...
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-border/50">
              <p className="text-xs text-muted-foreground mb-2">
                {isImageMode ? 'Describe changes and regenerate' : 'Describe what to change'}
              </p>
              <div className="flex gap-2">
                <textarea
                  ref={editTextareaRef}
                  value={editPrompt}
                  onChange={(e) => setEditPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleEditRequest();
                    }
                  }}
                  placeholder={isImageMode ? 'e.g., Use warmer colors...' : 'e.g., Change the header color...'}
                  rows={1}
                  className="flex-1 text-sm bg-transparent border border-border/50 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-purple-400/50"
                />
                <button
                  onClick={handleEditRequest}
                  disabled={!editPrompt.trim() || isRegenerating}
                  className="w-9 h-9 rounded-lg bg-purple-500 text-white flex items-center justify-center hover:bg-purple-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Present mode — fullscreen overlay */}
      {showPresentMode && hasContent && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col">
          <div className="flex items-center justify-between px-4 py-2 bg-black/80">
            <span className="text-white text-sm font-medium">{title}</span>
            <button onClick={() => setShowPresentMode(false)} className="text-white/70 hover:text-white p-2">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto flex justify-center bg-black">
            {isImageMode && infographicImage ? (
              <img src={infographicImage} alt={title} className="max-w-full h-auto" />
            ) : htmlContent ? (
              <iframe
                title="Infographic Present"
                srcDoc={htmlContent}
                style={{ width: canvasWidth, minHeight: '100%', border: 'none', background: 'white' }}
                sandbox="allow-scripts allow-same-origin"
              />
            ) : null}
          </div>
        </div>
      )}

      <ShareContentModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        contentType="infographic"
        title={title || 'Infographic'}
        userId={userId}
        htmlContent={htmlContent}
      />
    </div>
  );
});

export default InfographicWorkspace;
